import { create } from 'zustand';
import { engine } from '../core/audio/AudioEngine';
import { createDefaultProject } from '../core/project/defaultProject';
import {
  CHANNEL_COLORS,
  Bus,
  Channel,
  ChannelKind,
  EffectSlot,
  LoopRange,
  Note,
  Pattern,
  PlaylistClip,
  Project,
  SamplerParams,
  SynthParams,
  defaultSynthParams,
  uid,
} from '../core/project/types';
import { EFFECTS } from '../effects/registry';
import { defaultParams } from '../effects/types';
import { sampleBank } from '../core/samples/SampleBank';
import { FxPresetEffect } from '../presets/fxPresets';
import { SynthPreset } from '../presets/synthPresets';
import {
  downloadBlob,
  exportZip,
  importZip,
  normalizeProject,
  projectToJson,
} from '../core/persistence/projectIO';
import { undoManager } from '../core/undo/UndoManager';
import { encodeWav } from '../core/rendering/wav';
import { encodeMp3 } from '../core/rendering/mp3';

interface AppState {
  project: Project;
  selectedChannelId: string | null;
  isPlaying: boolean;
  audioReady: boolean;
  recording: { active: boolean; source: 'mic' | 'internal' | null };
  busy: string | null;
  canUndo: boolean;
  canRedo: boolean;

  selectChannel: (id: string | null) => void;
  addChannel: (kind: ChannelKind) => void;
  removeChannel: (id: string) => void;
  updateChannel: (id: string, patch: Partial<Channel>) => void;
  updateSynth: (id: string, patch: Partial<SynthParams>) => void;
  updateSampler: (id: string, patch: Partial<SamplerParams>) => void;
  importSample: (file: File) => Promise<void>;
  applySynthPreset: (preset: SynthPreset) => void;
  assignSample: (sampleId: string) => void;
  applyFxPreset: (effects: FxPresetEffect[]) => void;
  toggleRecording: (source: 'mic' | 'internal') => Promise<void>;
  bounce: (format: 'wav' | 'mp3') => Promise<void>;
  saveProject: () => void;
  loadProjectFile: (file: File) => Promise<void>;
  exportZipFile: () => Promise<void>;
  importZipFile: (file: File) => Promise<void>;
  loadProject: (project: Project) => void;
  undo: () => void;
  redo: () => void;
  toggleStep: (patternId: string, step: number, note: number) => void;
  addNote: (patternId: string, start: number, note: number, length?: number, velocity?: number) => void;
  updateNote: (
    patternId: string,
    noteId: string,
    patch: Partial<Pick<Note, 'start' | 'length' | 'note' | 'velocity'>>,
  ) => void;
  removeNote: (patternId: string, noteId: string) => void;
  setPatternLength: (patternId: string, len: number) => void;
  newPattern: () => void;
  addClip: (channelId: string, startStep: number, patternId: string, length: number) => void;
  updateClip: (clipId: string, patch: Partial<Pick<PlaylistClip, 'startStep' | 'length'>>) => void;
  removeClip: (clipId: string) => void;
  togglePlay: () => Promise<void>;
  stop: () => void;
  setBpm: (bpm: number) => void;
  setLoop: (patch: Partial<LoopRange>) => void;
  setMasterVolume: (v: number) => void;
  setCountIn: (bars: number) => void;
  setTimeSig: (beats: number, unit: number) => void;
  addBus: () => void;
  removeBus: (id: string) => void;
  updateBus: (id: string, patch: Partial<Bus>) => void;
  addEffect: (target: EffectTarget, type: string) => void;
  removeEffect: (target: EffectTarget, effectId: string) => void;
  toggleEffect: (target: EffectTarget, effectId: string) => void;
  updateEffect: (target: EffectTarget, effectId: string, patch: Record<string, number>) => void;
  setSend: (channelId: string, busId: string, level: number) => void;

  // Session view (clip launch grid)
  sessionActive: Record<string, string>; // channelId → active patternId
  sessionActiveScene: number | null; // index of the launched scene row
  launchScene: (idx: number) => Promise<void>;
  launchCell: (channelId: string, patternId: string) => Promise<void>;
  stopCell: (channelId: string) => void;
  stopSession: () => void;
  sceneToggle: (idx: number) => Promise<void>;
  cellToggle: (channelId: string, patternId: string) => Promise<void>;
  addScene: () => void;
  removeScene: (id: string) => void;
  renameScene: (id: string, name: string) => void;
  setSceneCell: (sceneId: string, channelId: string, patternId: string | null) => void;
  createPattern: (name?: string) => string;
}

/** A channel or bus — both host insert effect chains. */
export interface EffectTarget {
  kind: 'channel' | 'bus';
  id: string;
}

function commit(
  set: (p: Partial<AppState>) => void,
  get: () => AppState,
  project: Project,
  group?: string,
): void {
  undoManager.push(get().project, group);
  set({ project, canUndo: undoManager.canUndo, canRedo: undoManager.canRedo });
  engine.setProject(project);
}

export const useAppStore = create<AppState>((set, get) => {
  const initial = createDefaultProject();
  engine.setProject(initial);

  return {
    project: initial,
    selectedChannelId: initial.channels[0]?.id ?? null,
    isPlaying: false,
    audioReady: false,
    recording: { active: false, source: null },
    busy: null,
    canUndo: false,
    canRedo: false,
    sessionActive: {},
    sessionActiveScene: null,

    selectChannel: (id) => set({ selectedChannelId: id }),

    addChannel: (kind) => {
      const p = get().project;
      const patternId = uid();
      const pattern: Pattern = {
        id: patternId,
        name: `Pattern ${Object.keys(p.patterns).length + 1}`,
        length: 16,
        notes: [],
      };
      const count = p.channels.filter((c) => c.kind === kind).length + 1;
      const name =
        kind === 'drum' ? `Drum ${count}` : kind === 'sampler' ? `Sampler ${count}` : `Synth ${count}`;
      const channel: Channel = {
        id: uid(),
        name,
        kind,
        color: CHANNEL_COLORS[p.channels.length % CHANNEL_COLORS.length],
        synth: defaultSynthParams(),
        drum: { kit: 'classic' },
        sampler: { sampleId: 'builtin-pluck', rootNote: 60, loop: false },
        patternId,
        muted: false,
        solo: false,
        volume: 0.9,
        pan: 0,
        effects: [],
        sends: {},
      };
      commit(
        set,
        get,
        {
          ...p,
          patterns: { ...p.patterns, [patternId]: pattern },
          channels: [...p.channels, channel],
        },
      );
      set({ selectedChannelId: channel.id });
    },

    removeChannel: (id) => {
      const p = get().project;
      const ch = p.channels.find((c) => c.id === id);
      if (!ch) return;
      const channels = p.channels.filter((c) => c.id !== id);
      const patterns = { ...p.patterns };
      if (!channels.some((c) => c.patternId === ch.patternId)) {
        delete patterns[ch.patternId];
      }
      commit(set, get, { ...p, channels, patterns });
      const selected = get().selectedChannelId;
      if (selected === id) {
        set({ selectedChannelId: channels[0]?.id ?? null });
      }
    },

    updateChannel: (id, patch) => {
      const p = get().project;
      commit(
        set,
        get,
        {
          ...p,
          channels: p.channels.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        },
        'mix',
      );
    },

    updateSynth: (id, patch) => {
      const p = get().project;
      commit(
        set,
        get,
        {
          ...p,
          channels: p.channels.map((c) =>
            c.id === id ? { ...c, synth: { ...c.synth, ...patch } } : c,
          ),
        },
        'synth',
      );
    },

    updateSampler: (id, patch) => {
      const p = get().project;
      commit(
        set,
        get,
        {
          ...p,
          channels: p.channels.map((c) =>
            c.id === id ? { ...c, sampler: { ...c.sampler, ...patch } } : c,
          ),
        },
        'sampler',
      );
    },

    importSample: async (file) => {
      const ctx = await engine.ensureContext();
      const data = await file.arrayBuffer();
      const buffer = await ctx.decodeAudioData(data);
      const name = file.name.replace(/\.[^.]+$/, '');
      const id = await sampleBank.addFromBuffer(name, buffer);
      const sel = get().selectedChannelId;
      const ch = get().project.channels.find((c) => c.id === sel);
      if (ch && ch.kind === 'sampler') {
        get().updateSampler(ch.id, { sampleId: id });
      }
    },

    applySynthPreset: (preset) => {
      const sel = get().selectedChannelId;
      const ch = get().project.channels.find((c) => c.id === sel);
      if (!ch || ch.kind !== 'synth') return;
      get().updateSynth(ch.id, preset.params);
    },

    assignSample: (sampleId) => {
      const sel = get().selectedChannelId;
      const ch = get().project.channels.find((c) => c.id === sel);
      if (!ch || ch.kind !== 'sampler') return;
      const s = sampleBank.get(sampleId);
      get().updateSampler(ch.id, {
        sampleId,
        rootNote: s?.rootNote ?? ch.sampler.rootNote,
      });
    },

    applyFxPreset: (effects) => {
      const p = get().project;
      const sel = get().selectedChannelId;
      const ch = p.channels.find((c) => c.id === sel);
      if (!ch) return;
      const slots: EffectSlot[] = effects
        .filter((e) => EFFECTS[e.type])
        .map((e) => ({
          id: uid(),
          type: e.type,
          enabled: true,
          params: { ...defaultParams(EFFECTS[e.type]), ...e.params },
        }));
      commit(set, get, {
        ...p,
        channels: p.channels.map((c) => (c.id === ch.id ? { ...c, effects: slots } : c)),
      });
    },

    toggleRecording: async (source) => {
      if (get().recording.active) {
        const buf = await engine.stopRecording();
        set({ recording: { active: false, source: null } });
        if (buf) {
          const id = await sampleBank.addFromBuffer(
            `Recording ${new Date().toLocaleTimeString()}`,
            buf,
          );
          const ch = get().project.channels.find((c) => c.id === get().selectedChannelId);
          if (ch?.kind === 'sampler') {
            get().updateSampler(ch.id, { sampleId: id });
          }
        }
      } else {
        await engine.startRecording(source);
        set({ recording: { active: true, source } });
      }
    },

    bounce: async (format) => {
      if (get().busy) return;
      set({ busy: 'Bouncing…' });
      try {
        const { channels, sampleRate } = await engine.bounce();
        if (format === 'wav') {
          const wav = encodeWav(channels, sampleRate);
          downloadBlob(new Blob([wav], { type: 'audio/wav' }), 'bounce.wav');
        } else {
          const mp3 = await encodeMp3(channels, sampleRate);
          downloadBlob(new Blob([mp3], { type: 'audio/mpeg' }), 'bounce.mp3');
        }
      } catch (e) {
        console.error('bounce failed', e);
      } finally {
        set({ busy: null });
      }
    },

    saveProject: () => {
      const p = get().project;
      const safeName = (p.name || 'project').replace(/[^\w\- ]+/g, '').trim() || 'project';
      downloadBlob(
        new Blob([projectToJson(p)], { type: 'application/json' }),
        `${safeName}.zangle.json`,
      );
    },

    loadProjectFile: async (file) => {
      try {
        const project = normalizeProject(JSON.parse(await file.text()));
        get().loadProject(project);
      } catch (e) {
        console.error('project load failed', e);
      }
    },

    exportZipFile: async () => {
      set({ busy: 'Exporting…' });
      try {
        await exportZip(get().project, sampleBank);
      } catch (e) {
        console.error('export failed', e);
      } finally {
        set({ busy: null });
      }
    },

    importZipFile: async (file) => {
      set({ busy: 'Importing…' });
      try {
        const { project, samples } = await importZip(file);
        const ctx = await engine.ensureContext();
        for (const s of samples) {
          const buf = await ctx.decodeAudioData(s.wav);
          sampleBank.restore(s.id, s.name, buf, s.rootNote);
        }
        get().loadProject(project);
      } catch (e) {
        console.error('import failed', e);
      } finally {
        set({ busy: null });
      }
    },

    loadProject: (project) => {
      undoManager.clear();
      engine.stopSession();
      commit(set, get, project);
      set({
        selectedChannelId: project.channels[0]?.id ?? null,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
        sessionActive: {},
        sessionActiveScene: null,
      });
    },

    undo: () => {
      const prev = undoManager.undo(get().project);
      if (!prev) return;
      set({
        project: prev,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      engine.setProject(prev);
      const sel = get().selectedChannelId;
      if (sel && !prev.channels.some((c) => c.id === sel)) {
        set({ selectedChannelId: prev.channels[0]?.id ?? null });
      }
    },

    redo: () => {
      const next = undoManager.redo(get().project);
      if (!next) return;
      set({
        project: next,
        canUndo: undoManager.canUndo,
        canRedo: undoManager.canRedo,
      });
      engine.setProject(next);
      const sel = get().selectedChannelId;
      if (sel && !next.channels.some((c) => c.id === sel)) {
        set({ selectedChannelId: next.channels[0]?.id ?? null });
      }
    },

    toggleStep: (patternId, step, note) => {
      const p = get().project;
      const pattern = p.patterns[patternId];
      if (!pattern) return;
      const existing = pattern.notes.find((n) => n.start === step && n.note === note);
      const notes = existing
        ? pattern.notes.filter((n) => n.id !== existing.id)
        : [...pattern.notes, { id: uid(), start: step, length: 1, note, velocity: 100 }];
      commit(
        set,
        get,
        {
          ...p,
          patterns: { ...p.patterns, [patternId]: { ...pattern, notes } },
        },
        'steps',
      );
    },

    addNote: (patternId, start, note, length = 1, velocity = 100) => {
      const p = get().project;
      const pattern = p.patterns[patternId];
      if (!pattern) return;
      const n: Note = {
        id: uid(),
        start: Math.max(0, Math.min(start, pattern.length - 1)),
        length: Math.max(1, Math.min(length, pattern.length - Math.max(0, start))),
        note: Math.max(0, Math.min(127, note)),
        velocity,
      };
      commit(set, get, {
        ...p,
        patterns: { ...p.patterns, [patternId]: { ...pattern, notes: [...pattern.notes, n] } },
      });
    },

    updateNote: (patternId, noteId, patch) => {
      const p = get().project;
      const pattern = p.patterns[patternId];
      if (!pattern) return;
      const notes = pattern.notes.map((n) => {
        if (n.id !== noteId) return n;
        const next = { ...n, ...patch };
        next.start = Math.max(0, Math.min(next.start, pattern.length - 1));
        next.length = Math.max(1, Math.min(next.length, pattern.length - next.start));
        next.note = Math.max(0, Math.min(127, next.note));
        next.velocity = Math.max(1, Math.min(127, next.velocity));
        return next;
      });
      commit(
        set,
        get,
        {
          ...p,
          patterns: { ...p.patterns, [patternId]: { ...pattern, notes } },
        },
        'note',
      );
    },

    removeNote: (patternId, noteId) => {
      const p = get().project;
      const pattern = p.patterns[patternId];
      if (!pattern) return;
      commit(set, get, {
        ...p,
        patterns: {
          ...p.patterns,
          [patternId]: { ...pattern, notes: pattern.notes.filter((n) => n.id !== noteId) },
        },
      });
    },

    setPatternLength: (patternId, len) => {
      const p = get().project;
      const pattern = p.patterns[patternId];
      if (!pattern) return;
      const notes = pattern.notes
        .filter((n) => n.start < len)
        .map((n) => ({ ...n, length: Math.min(n.length, len - n.start) }));
      commit(set, get, {
        ...p,
        patterns: { ...p.patterns, [patternId]: { ...pattern, length: len, notes } },
      });
    },

    newPattern: () => {
      const p = get().project;
      const id = get().selectedChannelId;
      const ch = p.channels.find((c) => c.id === id);
      if (!ch) return;
      const patternId = uid();
      const pattern: Pattern = {
        id: patternId,
        name: `Pattern ${Object.keys(p.patterns).length + 1}`,
        length: 16,
        notes: [],
      };
      commit(set, get, {
        ...p,
        patterns: { ...p.patterns, [patternId]: pattern },
        channels: p.channels.map((c) => (c.id === ch.id ? { ...c, patternId } : c)),
      });
    },

    addClip: (channelId, startStep, patternId, length) => {
      const p = get().project;
      const clip: PlaylistClip = {
        id: uid(),
        channelId,
        patternId,
        startStep: Math.max(0, Math.floor(startStep)),
        length: Math.max(1, Math.floor(length)),
      };
      commit(set, get, { ...p, playlist: [...p.playlist, clip] });
    },

    updateClip: (clipId, patch) => {
      const p = get().project;
      const playlist = p.playlist.map((c) => {
        if (c.id !== clipId) return c;
        const next = { ...c, ...patch };
        next.startStep = Math.max(0, Math.floor(next.startStep));
        next.length = Math.max(1, Math.floor(next.length));
        return next;
      });
      commit(set, get, { ...p, playlist }, 'clip');
    },

    removeClip: (clipId) => {
      const p = get().project;
      commit(set, get, { ...p, playlist: p.playlist.filter((c) => c.id !== clipId) });
    },

    togglePlay: async () => {
      const { isPlaying } = get();
      if (isPlaying) {
        engine.stop();
        set({ isPlaying: false, sessionActiveScene: null });
      } else {
        await engine.play();
        set({ isPlaying: true, audioReady: true });
      }
    },

    stop: () => {
      engine.stop();
      // Session clips stay armed in the engine (Play resumes them), but the
      // scene row is no longer the one driving playback.
      set({ isPlaying: false, sessionActiveScene: null });
    },

    setBpm: (bpm) => {
      const p = get().project;
      commit(set, get, { ...p, bpm });
    },

    setLoop: (patch) => {
      const p = get().project;
      commit(set, get, { ...p, loop: { ...p.loop, ...patch } });
    },

    setMasterVolume: (v) => {
      const p = get().project;
      commit(set, get, { ...p, masterVolume: v });
    },

    setCountIn: (bars) => {
      const p = get().project;
      commit(set, get, { ...p, countInBars: bars });
    },

    setTimeSig: (beats, unit) => {
      const p = get().project;
      commit(set, get, { ...p, timeSig: { beats, unit } });
    },

    addBus: () => {
      const p = get().project;
      const bus: Bus = {
        id: uid(),
        name: `Bus ${p.buses.length + 1}`,
        volume: 0.9,
        pan: 0,
        effects: [],
      };
      commit(set, get, { ...p, buses: [...p.buses, bus] });
    },

    removeBus: (id) => {
      const p = get().project;
      const buses = p.buses.filter((b) => b.id !== id);
      const channels = p.channels.map((c) => {
        if (!(id in c.sends)) return c;
        const sends = { ...c.sends };
        delete sends[id];
        return { ...c, sends };
      });
      commit(set, get, { ...p, buses, channels });
    },

    updateBus: (id, patch) => {
      const p = get().project;
      commit(set, get, {
        ...p,
        buses: p.buses.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      }, 'bus');
    },

    addEffect: (target, type) => {
      const def = EFFECTS[type];
      if (!def) return;
      const p = get().project;
      const slot = { id: uid(), type, enabled: true, params: defaultParams(def) };
      if (target.kind === 'channel') {
        commit(set, get, {
          ...p,
          channels: p.channels.map((c) =>
            c.id === target.id ? { ...c, effects: [...c.effects, slot] } : c,
          ),
        });
      } else {
        commit(set, get, {
          ...p,
          buses: p.buses.map((b) => (b.id === target.id ? { ...b, effects: [...b.effects, slot] } : b)),
        });
      }
    },

    removeEffect: (target, effectId) => {
      const p = get().project;
      const filter = (arr: EffectSlot[]) => arr.filter((e) => e.id !== effectId);
      if (target.kind === 'channel') {
        commit(set, get, {
          ...p,
          channels: p.channels.map((c) => (c.id === target.id ? { ...c, effects: filter(c.effects) } : c)),
        });
      } else {
        commit(set, get, {
          ...p,
          buses: p.buses.map((b) => (b.id === target.id ? { ...b, effects: filter(b.effects) } : b)),
        });
      }
    },

    toggleEffect: (target, effectId) => {
      const p = get().project;
      const map = (arr: EffectSlot[]) =>
        arr.map((e) => (e.id === effectId ? { ...e, enabled: !e.enabled } : e));
      if (target.kind === 'channel') {
        commit(set, get, {
          ...p,
          channels: p.channels.map((c) => (c.id === target.id ? { ...c, effects: map(c.effects) } : c)),
        });
      } else {
        commit(set, get, {
          ...p,
          buses: p.buses.map((b) => (b.id === target.id ? { ...b, effects: map(b.effects) } : b)),
        });
      }
    },

    updateEffect: (target, effectId, patch) => {
      const p = get().project;
      const map = (arr: EffectSlot[]) =>
        arr.map((e) => (e.id === effectId ? { ...e, params: { ...e.params, ...patch } } : e));
      if (target.kind === 'channel') {
        commit(set, get, {
          ...p,
          channels: p.channels.map((c) => (c.id === target.id ? { ...c, effects: map(c.effects) } : c)),
        }, 'fx');
      } else {
        commit(set, get, {
          ...p,
          buses: p.buses.map((b) => (b.id === target.id ? { ...b, effects: map(b.effects) } : b)),
        }, 'fx');
      }
    },

    setSend: (channelId, busId, level) => {
      const p = get().project;
      commit(set, get, {
        ...p,
        channels: p.channels.map((c) =>
          c.id === channelId ? { ...c, sends: { ...c.sends, [busId]: level } } : c,
        ),
      }, 'send');
    },

    // ── Session view (clip launch grid) ───────────────────────────────────

    launchScene: async (idx) => {
      const p = get().project;
      const scene = p.scenes[idx];
      if (!scene) return;
      const cells: Record<string, string | null> = {};
      for (const ch of p.channels) cells[ch.id] = scene.cells[ch.id] ?? null;
      await engine.launchScene(cells);
      const active: Record<string, string> = {};
      for (const [chId, patId] of Object.entries(cells)) {
        if (patId) active[chId] = patId;
      }
      set({ isPlaying: true, audioReady: true, sessionActive: active, sessionActiveScene: idx });
    },

    launchCell: async (channelId, patternId) => {
      await engine.launchCell(channelId, patternId);
      set({
        isPlaying: true,
        audioReady: true,
        sessionActive: { ...get().sessionActive, [channelId]: patternId },
        sessionActiveScene: null,
      });
    },

    stopCell: (channelId) => {
      engine.stopCell(channelId);
      const active = { ...get().sessionActive };
      delete active[channelId];
      set({
        sessionActive: active,
        sessionActiveScene: null,
        isPlaying: Object.keys(active).length > 0,
      });
    },

    stopSession: () => {
      engine.stopSession();
      set({ isPlaying: false, sessionActive: {}, sessionActiveScene: null });
    },

    sceneToggle: async (idx) => {
      if (get().sessionActiveScene === idx) get().stopSession();
      else await get().launchScene(idx);
    },

    cellToggle: async (channelId, patternId) => {
      if (get().sessionActive[channelId] === patternId) get().stopCell(channelId);
      else await get().launchCell(channelId, patternId);
    },

    addScene: () => {
      const p = get().project;
      const scene = {
        id: uid(),
        name: `Scene ${p.scenes.length + 1}`,
        cells: {} as Record<string, string | null>,
      };
      commit(set, get, { ...p, scenes: [...p.scenes, scene] }, 'scene');
    },

    removeScene: (id) => {
      const p = get().project;
      commit(set, get, { ...p, scenes: p.scenes.filter((s) => s.id !== id) }, 'scene');
    },

    renameScene: (id, name) => {
      const p = get().project;
      commit(set, get, {
        ...p,
        scenes: p.scenes.map((s) => (s.id === id ? { ...s, name } : s)),
      }, 'scene');
    },

    setSceneCell: (sceneId, channelId, patternId) => {
      const p = get().project;
      commit(set, get, {
        ...p,
        scenes: p.scenes.map((s) =>
          s.id === sceneId ? { ...s, cells: { ...s.cells, [channelId]: patternId } } : s,
        ),
      }, 'scene');
    },

    createPattern: (name) => {
      const p = get().project;
      const patternId = uid();
      const pattern: Pattern = {
        id: patternId,
        name: name ?? `Pattern ${Object.keys(p.patterns).length + 1}`,
        length: 16,
        notes: [],
      };
      commit(set, get, { ...p, patterns: { ...p.patterns, [patternId]: pattern } }, 'scene');
      return patternId;
    },
  };
});